import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { PanierContext } from "../contexts/PanierContext";
import { ProfilContext } from "../contexts/ProfilContext";
import { auditPrice } from "../utils/priceAuditor";
import { useProfilPricing } from "../hooks/useProfilPricing";
import { formatCHF } from "../utils/formatCHF";
import { downloadExistingInvoice } from "../utils/downloadExistingInvoice";
import { trackFunnelEventBatch } from "../utils/trackFunnelBatch";

/* ============================================================================
   RÈGLES MÉTIER MAGASIN
   ----------------------------------------------------------------------------
   Ces helpers définissent les jours d’ouverture et les créneaux disponibles.
   Ils restent côté front pour guider l’UX avant validation backend.
============================================================================ */

const DAY_NAMES = [
  "dimanche",
  "lundi",
  "mardi",
  "mercredi",
  "jeudi",
  "vendredi",
  "samedi",
];

function isStoreOpenDay(dayIndex) {
  return dayIndex >= 2 && dayIndex <= 6;
}

function getPickupSlotsForDay(dayIndex) {
  if (dayIndex >= 2 && dayIndex <= 5) {
    return [
      { value: "08:00-10:00", label: "08h00 - 10h00", startMinutes: 8 * 60 },
      { value: "10:00-12:00", label: "10h00 - 12h00", startMinutes: 10 * 60 },
      {
        value: "12:00-13:15",
        label: "12h00 - 13h15",
        startMinutes: 12 * 60,
      },
      { value: "14:00-16:00", label: "14h00 - 16h00", startMinutes: 14 * 60 },
      {
        value: "16:00-18:30",
        label: "16h00 - 18h30",
        startMinutes: 16 * 60,
      },
    ];
  }

  if (dayIndex === 6) {
    return [
      { value: "07:00-09:30", label: "07h00 - 09h30", startMinutes: 7 * 60 },
      {
        value: "09:30-12:00",
        label: "09h30 - 12h00",
        startMinutes: 9 * 60 + 30,
      },
      { value: "12:00-15:00", label: "12h00 - 15h00", startMinutes: 12 * 60 },
      {
        value: "15:00-17:30",
        label: "15h00 - 17h30",
        startMinutes: 15 * 60,
      },
    ];
  }

  return [];
}

function addDays(baseDate, offset) {
  const d = new Date(baseDate);
  d.setDate(d.getDate() + offset);
  return d;
}

function formatChoiceLabel(offset, dayIndex, dateObj) {
  if (offset === 0) return "Aujourd’hui";
  if (offset === 1) return "Demain";

  const dayName = DAY_NAMES[dayIndex] || "";
  const dateLabel = dateObj.toLocaleDateString("fr-CH", {
    day: "2-digit",
    month: "2-digit",
  });

  return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dateLabel}`;
}

function getNextOpenOffsets(now, count = 2, includeTodayIfOpen = true) {
  const offsets = [];

  for (let offset = 0; offset < 10; offset += 1) {
    const d = addDays(now, offset);
    const dayIndex = d.getDay();

    if (!isStoreOpenDay(dayIndex)) continue;
    if (!includeTodayIfOpen && offset === 0) continue;

    offsets.push(offset);
    if (offsets.length >= count) break;
  }

  return offsets;
}

/* ============================================================================
   STYLES LOCAUX DU TUNNEL
   ----------------------------------------------------------------------------
   Ces styles compactent uniquement TunnelCommande.js.
   Aucun CSS global n’est modifié.
============================================================================ */

const tunnelStyles = {
  container: {
    maxWidth: 980,
    margin: "34px auto 52px",
    padding: "0 18px",
  },

  visitorBanner: {
    maxWidth: 980,
    margin: "0 auto 14px",
    padding: "9px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,.10)",
    background: "rgba(0,0,0,.025)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },

  title: {
    margin: "0 0 20px",
    lineHeight: 1.15,
  },

  content: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 318px",
    gap: 22,
    alignItems: "start",
  },

  leftColumn: {
    minWidth: 0,
  },

  section: {
    marginTop: 0,
    marginBottom: 12,
    padding: "14px 16px",
    borderRadius: 14,
  },

  sectionSpaced: {
    marginTop: 10,
    marginBottom: 12,
    padding: "14px 16px",
    borderRadius: 14,
  },

  sectionTitle: {
    marginTop: 0,
    marginBottom: 14,
    fontSize: "1rem",
    lineHeight: 1.25,
  },

  modeSelector: {
    display: "flex",
    gap: 10,
    marginBottom: 16,
    flexWrap: "wrap",
  },

  infoBox: {
    marginBottom: 16,
    padding: "10px 12px",
    borderRadius: 10,
    background: "rgba(212,175,55,0.08)",
    border: "1px solid rgba(212,175,55,0.22)",
    color: "#3a2a16",
    fontSize: 13,
    lineHeight: 1.45,
  },

  pickpayBox: {
    padding: 12,
    background: "#f9f9f9",
    borderRadius: 8,
    border: "1px solid rgba(0,0,0,0.06)",
  },

  fieldBlock: {
    marginBottom: 12,
  },

  label: {
    display: "block",
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 700,
  },

  select: {
    width: "100%",
    padding: "9px 10px",
  },

  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },

  formGroupFull: {
    gridColumn: "span 2",
  },

  textarea: {
    width: "100%",
    height: 82,
    padding: 10,
    borderRadius: 8,
    border: "1px solid #ddd",
    resize: "vertical",
  },

  summary: {
    position: "sticky",
    top: 18,
    alignSelf: "start",
    padding: "14px 15px",
    borderRadius: 14,
  },

  paymentBox: {
    marginTop: 10,
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.08)",
    background: "rgba(0,0,0,0.025)",
  },

  paymentText: {
    marginTop: 9,
    fontSize: "0.76rem",
    color: "rgba(0,0,0,0.62)",
    lineHeight: 1.45,
  },

  checkoutButton: {
    marginTop: 16,
  },

  backButton: {
    marginTop: 10,
  },
};

/* ============================================================================
   NORMALISATION CENTIMES
============================================================================ */

function toCents(value) {
  if (value === null || value === undefined || value === "") return null;

  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;

  if (Number.isInteger(n) && n >= 100) {
    return Math.round(n);
  }

  return Math.round(n * 100);
}

/* ============================================================================
   HELPERS PAYLOAD COMMANDE
============================================================================ */

function normalizeString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function normalizeArray(value) {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v).trim()).filter(Boolean);
}

function buildCommandePanierItem(item) {
  const qty = Number(item?.qty || item?.quantite || item?.quantity || 1) || 1;
  const grams = Number(item?.grams || 0) || 0;

  const appliedPrice =
    toCents(item?.audit?.displayPrice) ??
    toCents(item?.appliedPrice) ??
    toCents(item?.displayPrice) ??
    toCents(item?.price) ??
    0;

  const basePrice =
    toCents(item?.audit?.basePrice) ??
    toCents(item?.basePrice) ??
    toCents(item?.publicPrice) ??
    toCents(item?.old_price) ??
    toCents(item?.price) ??
    appliedPrice;

  const memberPrice =
    toCents(item?.audit?.targetPrice) ??
    toCents(item?.memberPrice) ??
    toCents(item?.pricePro) ??
    toCents(item?.prix_connecte) ??
    toCents(item?.prix_membre) ??
    null;

  return {
    id: item?.id ?? null,
    product_id: item?.product_id ?? item?.id ?? null,
    productId: item?.productId ?? item?.product_id ?? item?.id ?? null,
    produitId: item?.produitId ?? null,

    name: normalizeString(item?.name || item?.nom || item?.product_name),
    nom: normalizeString(item?.nom || item?.name || item?.product_name),
    title: normalizeString(item?.title || item?.name || item?.nom),
    product_name: normalizeString(
      item?.product_name || item?.name || item?.nom
    ),
    produitNom: normalizeString(item?.produitNom || item?.name || item?.nom),
    details_label: normalizeString(item?.details_label),
    isFondue: Boolean(item?.isFondue),
    fondueConfig: item?.fondueConfig ?? null,

    qty,
    quantite: qty,
    quantity: qty,
    grams,
    pieces: Number(item?.pieces || 0) || 0,

    price: appliedPrice,
    prix: appliedPrice,
    appliedPrice,
    basePrice,
    publicPrice: basePrice,
    memberPrice,
    pricingMode: item?.audit?.pricingMode || item?.pricingMode || "PUBLIC",

    pricePro:
      toCents(item?.pricePro) ??
      toCents(item?.memberPrice) ??
      toCents(item?.audit?.targetPrice),

    prix_connecte:
      toCents(item?.prix_connecte) ??
      toCents(item?.memberPrice) ??
      toCents(item?.pricePro) ??
      toCents(item?.audit?.targetPrice),

    prix_membre:
      toCents(item?.prix_membre) ??
      toCents(item?.memberPrice) ??
      toCents(item?.pricePro) ??
      toCents(item?.audit?.targetPrice),

    item_type: item?.item_type || item?.type || null,
    type: item?.type || item?.item_type || null,
    typeProduit: item?.typeProduit || null,
    source_table: item?.source_table || null,
    category: item?.category || null,

    isPlateau: Boolean(item?.isPlateau),
    isPlatter: Boolean(item?.isPlatter),
    isConfiguredPlateau: Boolean(item?.isConfiguredPlateau),
    isVirtualPlatter: Boolean(item?.isVirtualPlatter),

    signature: item?.signature || null,
    plateauSignature: item?.plateauSignature || null,

    plateau_personnes:
      item?.plateau_personnes ??
      item?.options?.personnes ??
      item?.plateauConfig?.personnes ??
      0,

    plateau_fromages:
      item?.plateau_fromages ??
      item?.options?.fromages ??
      item?.plateauConfig?.fromages ??
      "",

    plateau_laits_eviter: normalizeArray(
      item?.plateau_laits_eviter ??
        item?.options?.laitsEviter ??
        item?.plateauConfig?.laitsEviter ??
        []
    ),

    options: item?.options ?? null,
    plateauConfig: item?.plateauConfig ?? null,
    composition: item?.composition ?? null,
  };
}

/* ============================================================================
   COMPOSANT PRINCIPAL
============================================================================ */

const TunnelCommande = ({ userData, setUserData, setVue, t = {} }) => {
  const { userProfil, authStatus } = useContext(ProfilContext);
  const { panier, viderPanier } = useContext(PanierContext);

  const profilPricing = useProfilPricing(userProfil);

  const [showUserInfoMobile, setShowUserInfoMobile] = useState(false);

  const [isMobileLayout, setIsMobileLayout] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 860;
  });

  useEffect(() => {
    const onResize = () => {
      setIsMobileLayout(window.innerWidth <= 860);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isGuest = useMemo(() => {
    const token = localStorage.getItem("user_token") || "";
    const hasIdentity = Boolean(
      userProfil?.uid || userProfil?.id || userProfil?.email
    );

    if (authStatus === "connected" && token && hasIdentity) {
      return false;
    }

    return true;
  }, [userProfil, authStatus]);

  const [localUserData, setLocalUserData] = useState(() =>
    userData && typeof userData === "object" ? userData : {}
  );

  const hasExternalUserData = typeof setUserData === "function";
  const effectiveUserData = hasExternalUserData
    ? userData || {}
    : localUserData;

  useEffect(() => {
    if (hasExternalUserData) return;
    if (userData && typeof userData === "object") setLocalUserData(userData);
  }, [userData, hasExternalUserData]);

  const updateUserData = useCallback(
    (patch) => {
      if (!patch || typeof patch !== "object") return;

      const applyPatch = (prev) => {
        const base = prev && typeof prev === "object" ? prev : {};
        const next = { ...base };

        Object.keys(patch).forEach((k) => {
          const v = patch[k];
          if (v === null || v === undefined) return;
          next[k] = typeof v === "number" ? String(v) : v;
        });

        return next;
      };

      if (hasExternalUserData) {
        setUserData((prev) => applyPatch(prev));
      } else {
        setLocalUserData((prev) => applyPatch(prev));
      }
    },
    [hasExternalUserData, setUserData]
  );

  useEffect(() => {
    if (!userProfil) return;

    const patch = {};
    const ud = effectiveUserData || {};

    if (!ud.prenom && userProfil.prenom) patch.prenom = userProfil.prenom;
    if (!ud.nom && userProfil.nom) patch.nom = userProfil.nom;
    if (!ud.email && userProfil.email) patch.email = userProfil.email;

    const profilTel =
      userProfil.telephone || userProfil.tel || userProfil.phone;
    const udTel = ud.telephone || ud.tel;
    if (!udTel && profilTel) patch.telephone = profilTel;

    if (!ud.adresse && (userProfil.adresse || userProfil.address)) {
      patch.adresse = userProfil.adresse || userProfil.address;
    }

    if (!ud.ville && (userProfil.ville || userProfil.city)) {
      patch.ville = userProfil.ville || userProfil.city;
    }

    if (!ud.npa && (userProfil.npa || userProfil.zip)) {
      patch.npa = userProfil.npa || userProfil.zip;
    }

    if (Object.keys(patch).length) updateUserData(patch);
  }, [userProfil, effectiveUserData, updateUserData]);

  /* ==========================================================================
     ÉTATS DE LIVRAISON, RETRAIT ET PAIEMENT
     --------------------------------------------------------------------------
     Le mode est désormais réellement sélectionnable :
     - pickpay   : retrait magasin avec jour + créneau
     - livraison : livraison à domicile avec jour + adresse
  ========================================================================== */

  const [modeLivraison, setModeLivraison] = useState("pickpay");

  const [horaireRecuperation, setHoraireRecuperation] = useState("");
  const [pickupDay, setPickupDay] = useState("");
  const [pickupSlot, setPickupSlot] = useState("");
  const [jourLivraison, setJourLivraison] = useState("");
  const [notesCommande, setNotesCommande] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [commandeTerminee, setCommandeTerminee] = useState(false);

  const [invoiceState, setInvoiceState] = useState("IDLE");
  const [invoiceMeta, setInvoiceMeta] = useState(null);
  const [invoiceError, setInvoiceError] = useState("");

  const handleModeLivraisonChange = useCallback(
    (nextMode) => {
      if (!["pickpay", "livraison"].includes(nextMode)) return;
      if (nextMode === modeLivraison) return;

      setModeLivraison(nextMode);

      if (nextMode === "pickpay") {
        setJourLivraison("");
      }

      if (nextMode === "livraison") {
        setPickupDay("");
        setPickupSlot("");
        setHoraireRecuperation("");
      }

      trackFunnelEventBatch("delivery_mode_selected", {
        mode_livraison: nextMode,
        has_pickup_slot: nextMode === "pickpay",
        has_delivery_address:
          nextMode === "livraison" &&
          Boolean(normalizeString(effectiveUserData?.adresse)),
      });
    },
    [modeLivraison, effectiveUserData]
  );

  const now = new Date();
  const currentDayIndex = now.getDay();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const isBeforeNoon = currentMinutes < 12 * 60;
  const todayIsOpen = isStoreOpenDay(currentDayIndex);

  const deliveryOptions = useMemo(() => {
    let offsets = [];

    if (todayIsOpen && isBeforeNoon) {
      offsets = getNextOpenOffsets(now, 2, true);
    } else {
      offsets = getNextOpenOffsets(now, 1, false);
    }

    return offsets.map((offset) => {
      const d = addDays(now, offset);
      const dayIndex = d.getDay();

      return {
        value: formatChoiceLabel(offset, dayIndex, d),
        label: formatChoiceLabel(offset, dayIndex, d),
        offset,
        dayIndex,
      };
    });
  }, [todayIsOpen, isBeforeNoon]);

  const pickupDayOptions = useMemo(() => {
    const offsets = getNextOpenOffsets(now, 2, true);

    return offsets.map((offset) => {
      const d = addDays(now, offset);
      const dayIndex = d.getDay();

      return {
        value: formatChoiceLabel(offset, dayIndex, d),
        label: formatChoiceLabel(offset, dayIndex, d),
        offset,
        dayIndex,
      };
    });
  }, []);

  const pickupSlotOptions = useMemo(() => {
    const selectedDay = pickupDayOptions.find((d) => d.value === pickupDay);
    if (!selectedDay) return [];

    const baseSlots = getPickupSlotsForDay(selectedDay.dayIndex);

    if (selectedDay.offset !== 0) {
      return baseSlots;
    }

    return baseSlots.filter((slot) => slot.startMinutes > currentMinutes);
  }, [pickupDay, pickupDayOptions, currentMinutes]);

  useEffect(() => {
    if (modeLivraison !== "pickpay") {
      setPickupDay("");
      setPickupSlot("");
      setHoraireRecuperation("");
      return;
    }

    if (pickupDay && pickupSlot) {
      setHoraireRecuperation(`${pickupDay} - ${pickupSlot}`);
    } else {
      setHoraireRecuperation("");
    }
  }, [modeLivraison, pickupDay, pickupSlot]);

  useEffect(() => {
    if (modeLivraison !== "livraison") return;

    if (!deliveryOptions.length) {
      setJourLivraison("");
      return;
    }

    const stillValid = deliveryOptions.some(
      (opt) => opt.value === jourLivraison
    );

    if (!stillValid) {
      setJourLivraison(deliveryOptions[0].value);
    }
  }, [modeLivraison, deliveryOptions, jourLivraison]);

  useEffect(() => {
    const stillValid = pickupDayOptions.some((opt) => opt.value === pickupDay);
    if (!stillValid) {
      setPickupDay("");
      setPickupSlot("");
      setHoraireRecuperation("");
    }
  }, [pickupDayOptions, pickupDay]);

  useEffect(() => {
    const stillValid = pickupSlotOptions.some(
      (opt) => opt.value === pickupSlot
    );
    if (!stillValid) {
      setPickupSlot("");
      setHoraireRecuperation("");
    }
  }, [pickupSlotOptions, pickupSlot]);

  const { totalItemsCents, panierAudite } = useMemo(() => {
    const audites = (panier || []).map((item) => {
      const audit = auditPrice(item, { profilPricing, userProfil });

      return {
        ...item,
        audit,
      };
    });

    const total = audites.reduce((acc, i) => {
      const qty = Number(i.qty || i.quantite || i.quantity || 1) || 1;
      const grams = Number(i.grams || 0) || 0;
      const gramsMultiplier = grams > 0 ? grams / 100 : 1;
      const unitPrice = Number(i.audit?.displayPrice || 0) || 0;

      return acc + Math.round(unitPrice * gramsMultiplier) * qty;
    }, 0);

    return {
      totalItemsCents: total,
      panierAudite: audites,
    };
  }, [panier, profilPricing, userProfil]);

  const SEUIL_LIVRAISON_GRATUITE = 7999;
  const FRAIS_LIVRAISON = 1900;

  const fraisPort =
    modeLivraison === "livraison" && totalItemsCents < SEUIL_LIVRAISON_GRATUITE
      ? FRAIS_LIVRAISON
      : 0;

  const totalFinal = totalItemsCents + fraisPort;

  const downloadInvoiceAgain = useCallback(async () => {
    if (!invoiceMeta?.invoiceId) return;

    try {
      setInvoiceError("");
      setInvoiceState("CREATING");
      await downloadExistingInvoice({ invoiceId: invoiceMeta.invoiceId });
      setInvoiceState("READY");
    } catch (e) {
      setInvoiceError(e?.message || "Impossible de télécharger la facture.");
      setInvoiceState("ERROR");
    }
  }, [invoiceMeta]);

  const parseJsonSafe = async (res) => {
    try {
      return await res.json();
    } catch {
      return null;
    }
  };

  const readTextSafe = async (res) => {
    try {
      return await res.text();
    } catch {
      return "";
    }
  };

  const executerPaiementEtValidation = async () => {
    if (isProcessing) return;

    trackFunnelEventBatch("payment_attempt", {
      cart_items: Array.isArray(panierAudite) ? panierAudite.length : 0,
      cart_total_cents: totalFinal || 0,
      mode_livraison: modeLivraison || "",
      is_guest: isGuest === true,
      has_pickup_slot: Boolean(horaireRecuperation),
      has_delivery_address:
        modeLivraison === "livraison" &&
        Boolean(normalizeString(effectiveUserData?.adresse)),
      has_user_required_info: Boolean(
        normalizeString(effectiveUserData?.prenom) &&
          normalizeString(effectiveUserData?.nom) &&
          normalizeString(
            effectiveUserData?.telephone || effectiveUserData?.tel || ""
          )
      ),
    });

    const prenom = normalizeString(effectiveUserData?.prenom);
    const nom = normalizeString(effectiveUserData?.nom);
    const tel = normalizeString(
      effectiveUserData?.telephone || effectiveUserData?.tel || ""
    );

    if (!prenom || !nom) {
      alert(t?.nameRequired || "Veuillez saisir votre prénom et votre nom.");
      return;
    }

    if (!tel) {
      alert(t?.phoneRequired || "Veuillez saisir un numéro de téléphone.");
      return;
    }

    if (modeLivraison === "livraison") {
      const adresse = normalizeString(effectiveUserData?.adresse);
      const npa = normalizeString(effectiveUserData?.npa);
      const ville = normalizeString(effectiveUserData?.ville);

      if (!adresse) {
        alert(t?.addressRequired || "Veuillez saisir une adresse de livraison.");
        return;
      }

      if (!npa || !ville) {
        alert("Veuillez saisir le NPA et la ville pour la livraison.");
        return;
      }

      if (!jourLivraison) {
        alert("Veuillez choisir un jour de livraison.");
        return;
      }
    }

    if (modeLivraison === "pickpay") {
      if (!pickupDay || !pickupSlot || !horaireRecuperation) {
        alert(
          t?.alertPickPayTime ||
            "Veuillez choisir le jour de retrait et un créneau horaire."
        );
        return;
      }
    }

    setIsProcessing(true);
    setInvoiceState("IDLE");
    setInvoiceMeta(null);
    setInvoiceError("");

    const payload = {
      modeLivraison,
      jourLivraison,
      horaireRecuperation,
      notesCommande,
      panier: panierAudite.map((item) => buildCommandePanierItem(item)),
      totalFinal: Number(totalFinal || 0),
      userUid: userProfil?.uid || userProfil?.id || null,
      userData: effectiveUserData,
    };

    try {
      const token = localStorage.getItem("user_token") || "";
      const authHeaders = { "Content-Type": "application/json" };
      if (token) authHeaders.Authorization = `Bearer ${token}`;

      const resOrder = await fetch(
        "https://marchedeluxe.ch/services/valider_commande.php",
        {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify(payload),
        }
      );

      const orderData = await parseJsonSafe(resOrder);

      if (!resOrder.ok || !orderData?.success) {
        const msg =
          orderData?.error ||
          orderData?.message ||
          `Erreur création commande (HTTP ${resOrder.status}).`;
        alert(msg);
        return;
      }

      const orderId =
        orderData?.order_id ?? orderData?.id ?? orderData?.commande_id ?? null;

      if (!orderId) {
        alert("Commande créée, mais order_id manquant.");
        return;
      }

      const checkoutPayload = {
        order_id: Number(orderId),
      };

      const resCheckout = await fetch(
        "https://marchedeluxe.ch/services/create_postfinance_checkout.php",
        {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify(checkoutPayload),
        }
      );

      const checkoutData = await parseJsonSafe(resCheckout);

      if (!resCheckout.ok) {
        const raw = await readTextSafe(resCheckout);
        const snippet = (raw || "").slice(0, 180).trim();

        const msg =
          checkoutData?.error ||
          checkoutData?.message ||
          (snippet
            ? `Erreur serveur paiement (HTTP ${resCheckout.status}): ${snippet}`
            : `Erreur serveur paiement (HTTP ${resCheckout.status}).`);

        alert(msg);
        return;
      }

      if (!checkoutData?.success) {
        alert(checkoutData?.error || "Impossible d'initialiser le paiement.");
        return;
      }

      if (!checkoutData?.url) {
        alert("Session de paiement créée, mais URL de redirection absente.");
        return;
      }

      viderPanier?.();
      window.location.assign(checkoutData.url);
    } catch (e) {
      alert("Erreur réseau / paiement.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (commandeTerminee) {
    return (
      <div
        className="commande-succes"
        style={{ textAlign: "center", padding: "70px 20px" }}
      >
        <div style={{ fontSize: "3.2rem" }}>✨</div>

        <h1 style={{ color: "#d4af37", fontFamily: "serif" }}>
          {t.thankYouOrder || "Merci pour votre commande"}
        </h1>

        <p>{t.orderRegistered || "Votre commande a bien été enregistrée."}</p>

        <div
          style={{
            margin: "20px auto 0",
            maxWidth: 500,
            textAlign: "left",
            padding: 15,
            borderRadius: 12,
            border: "1px solid rgba(212,175,55,.35)",
            background: "rgba(212,175,55,.06)",
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 8 }}>
            Facture (source de vérité)
          </div>

          {invoiceState === "IDLE" && (
            <div style={{ opacity: 0.85 }}>Facture en préparation…</div>
          )}

          {invoiceState === "CREATING" && (
            <div style={{ opacity: 0.85 }}>Génération de la facture…</div>
          )}

          {invoiceState === "READY" && (
            <>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, opacity: 0.8 }}>Numéro</div>
                <div style={{ fontWeight: 800 }}>
                  {invoiceMeta?.invoiceNumber || "—"}
                </div>
              </div>

              <button
                className="btn-luxury"
                onClick={downloadInvoiceAgain}
                type="button"
              >
                Télécharger la facture
              </button>
            </>
          )}

          {invoiceState === "ERROR" && (
            <>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 800, color: "#b00020" }}>
                  Facture non disponible
                </div>
                <div style={{ fontSize: 12, opacity: 0.85 }}>
                  {invoiceError ||
                    "Commande validée, mais la facture n’a pas pu être générée."}
                </div>
              </div>

              <button
                className="btn-luxury-outline"
                onClick={() => setVue("magasin")}
                type="button"
              >
                Continuer
              </button>
            </>
          )}
        </div>

        <button
          className="btn-luxury"
          style={{ marginTop: 18 }}
          onClick={() => setVue("magasin")}
          type="button"
        >
          {t.backToShop || "RETOUR AU MAGASIN"}
        </button>
      </div>
    );
  }

  const prenomClient = (effectiveUserData?.prenom || "").trim();
  const nomClient = (effectiveUserData?.nom || "").trim();
  const telClient = (
    effectiveUserData?.telephone ||
    effectiveUserData?.tel ||
    ""
  ).trim();

  const hasRequiredUserInfo = Boolean(prenomClient && nomClient && telClient);

  const shouldShowUserInfoSection = isMobileLayout
    ? showUserInfoMobile || !hasRequiredUserInfo
    : true;

  return (
    <div className="tunnel-container" style={tunnelStyles.container}>
      {isGuest ? (
        <div style={tunnelStyles.visitorBanner}>
          <div style={{ fontWeight: 900 }}>👤 VISITEUR</div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>
            Prénom, nom et téléphone sont requis pour préparer votre commande.
          </div>
        </div>
      ) : null}

      <h1 className="panier-titre" style={tunnelStyles.title}>
        {t.finalizeOrder || "FINALISER MA COMMANDE"}
      </h1>

      <div
        className="panier-content"
        style={{
          ...tunnelStyles.content,
          ...(isMobileLayout
            ? {
                gridTemplateColumns: "1fr",
                gap: 16,
              }
            : {}),
        }}
      >
        <div className="panier-items" style={tunnelStyles.leftColumn}>
          <section className="checkout-section" style={tunnelStyles.section}>
            <h3 style={tunnelStyles.sectionTitle}>
              {t.pickupMode || "1. Mode de récupération"}
            </h3>

            <div className="mode-selector" style={tunnelStyles.modeSelector}>
              <button
                type="button"
                className={
                  modeLivraison === "livraison"
                    ? "btn-luxury"
                    : "btn-luxury-outline"
                }
                onClick={() => handleModeLivraisonChange("livraison")}
              >
                Livraison à domicile
              </button>

              <button
                type="button"
                className={
                  modeLivraison === "pickpay"
                    ? "btn-luxury"
                    : "btn-luxury-outline"
                }
                onClick={() => handleModeLivraisonChange("pickpay")}
              >
                {t.pickPay || "Retrait Pick & Pay"}
              </button>
            </div>

            <div style={tunnelStyles.infoBox}>
              {modeLivraison === "pickpay" ? (
                <>
                  <strong>Information retrait :</strong> le Pick & Pay suit les
                  horaires d’ouverture du magasin. Choisissez d’abord le jour de
                  retrait, puis un créneau disponible.
                </>
              ) : (
                <>
                  <strong>Information livraison :</strong> la livraison est
                  disponible les jours d’ouverture. Les frais sont offerts dès{" "}
                  {formatCHF(SEUIL_LIVRAISON_GRATUITE)}.
                </>
              )}
            </div>

            {modeLivraison === "pickpay" && (
              <div className="pickpay-details" style={tunnelStyles.pickpayBox}>
                <div style={tunnelStyles.fieldBlock}>
                  <label style={tunnelStyles.label}>Jour de retrait</label>

                  <select
                    value={pickupDay}
                    onChange={(e) => setPickupDay(e.target.value)}
                    style={tunnelStyles.select}
                  >
                    <option value="">-- Sélectionner un jour --</option>
                    {pickupDayOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={tunnelStyles.label}>
                    {t.pickupTime || "Horaire de retrait :"}
                  </label>

                  <select
                    value={pickupSlot}
                    onChange={(e) => setPickupSlot(e.target.value)}
                    style={tunnelStyles.select}
                    disabled={!pickupDay}
                  >
                    <option value="">
                      {pickupDay
                        ? t.chooseSlot || "-- Sélectionner un créneau --"
                        : "-- Choisissez d’abord un jour --"}
                    </option>

                    {pickupSlotOptions.map((slot) => (
                      <option key={slot.value} value={slot.value}>
                        {slot.label}
                      </option>
                    ))}
                  </select>
                </div>

                {pickupDay && pickupSlotOptions.length === 0 ? (
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 13,
                      color: "#8a1f1f",
                      fontWeight: 700,
                    }}
                  >
                    Aucun créneau restant pour ce jour.
                  </div>
                ) : null}

                {horaireRecuperation ? (
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 13,
                      color: "#5a4b3f",
                      fontWeight: 700,
                    }}
                  >
                    Retrait sélectionné : {horaireRecuperation}
                  </div>
                ) : null}
              </div>
            )}

            {modeLivraison === "livraison" && (
              <div className="delivery-details" style={tunnelStyles.pickpayBox}>
                <div style={tunnelStyles.fieldBlock}>
                  <label style={tunnelStyles.label}>Jour de livraison</label>

                  <select
                    value={jourLivraison}
                    onChange={(e) => setJourLivraison(e.target.value)}
                    style={tunnelStyles.select}
                  >
                    <option value="">-- Sélectionner un jour --</option>
                    {deliveryOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div
                  style={{
                    marginTop: 10,
                    fontSize: 13,
                    color: "#5a4b3f",
                    fontWeight: 700,
                  }}
                >
                  Adresse à renseigner dans la section “Vos informations”.
                </div>
              </div>
            )}
          </section>

          <section
            className="checkout-section"
            style={tunnelStyles.sectionSpaced}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: shouldShowUserInfoSection ? 14 : 0,
              }}
            >
              <div>
                <h3 style={{ ...tunnelStyles.sectionTitle, marginBottom: 4 }}>
                  {t.yourInfo || "2. Vos informations"}
                </h3>

                {isMobileLayout && hasRequiredUserInfo ? (
                  <div style={{ fontSize: 12, color: "rgba(0,0,0,.62)" }}>
                    {prenomClient} {nomClient} · {telClient}
                  </div>
                ) : null}
              </div>

              {isMobileLayout ? (
                <button
                  type="button"
                  className="btn-luxury-outline"
                  onClick={() => setShowUserInfoMobile((v) => !v)}
                  style={{
                    padding: "8px 12px",
                    fontSize: 12,
                    whiteSpace: "nowrap",
                  }}
                >
                  {shouldShowUserInfoSection ? "Masquer" : "Modifier"}
                </button>
              ) : null}
            </div>

            {shouldShowUserInfoSection ? (
              <div
                className="form-grid"
                style={{
                  ...tunnelStyles.formGrid,
                  ...(isMobileLayout ? { gridTemplateColumns: "1fr" } : {}),
                }}
              >
                <div className="form-group">
                  <label>{t.firstName || "Prénom"}</label>
                  <input
                    type="text"
                    value={effectiveUserData?.prenom || ""}
                    autoComplete="given-name"
                    placeholder="Votre prénom"
                    onChange={(e) => updateUserData({ prenom: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>{t.lastName || "Nom"}</label>
                  <input
                    type="text"
                    value={effectiveUserData?.nom || ""}
                    autoComplete="family-name"
                    placeholder="Votre nom"
                    onChange={(e) => updateUserData({ nom: e.target.value })}
                  />
                </div>

                <div
                  className="form-group"
                  style={isMobileLayout ? {} : tunnelStyles.formGroupFull}
                >
                  <label>{t.phone || "Téléphone"}</label>
                  <input
                    type="tel"
                    value={
                      effectiveUserData?.telephone ||
                      effectiveUserData?.tel ||
                      ""
                    }
                    autoComplete="tel"
                    placeholder="+41 …"
                    onChange={(e) =>
                      updateUserData({ telephone: e.target.value, tel: "" })
                    }
                  />
                </div>

                {modeLivraison === "livraison" && (
                  <>
                    <div
                      className="form-group"
                      style={isMobileLayout ? {} : tunnelStyles.formGroupFull}
                    >
                      <label>{t.address || "Adresse complète"}</label>
                      <input
                        type="text"
                        value={effectiveUserData?.adresse || ""}
                        autoComplete="street-address"
                        placeholder="Rue, numéro…"
                        onChange={(e) =>
                          updateUserData({ adresse: e.target.value })
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label>{t.zip || "NPA"}</label>
                      <input
                        type="text"
                        value={effectiveUserData?.npa || ""}
                        autoComplete="postal-code"
                        placeholder="2000"
                        onChange={(e) =>
                          updateUserData({ npa: e.target.value })
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label>{t.city || "Ville"}</label>
                      <input
                        type="text"
                        value={effectiveUserData?.ville || ""}
                        autoComplete="address-level2"
                        placeholder="Neuchâtel"
                        onChange={(e) =>
                          updateUserData({ ville: e.target.value })
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </section>

          <section
            className="checkout-section"
            style={tunnelStyles.sectionSpaced}
          >
            <h3 style={tunnelStyles.sectionTitle}>
              {t.prepNotes || "3. Instructions particulières"}
            </h3>

            <textarea
              placeholder={t.deliveryPlaceholder || "précisions..."}
              value={notesCommande}
              onChange={(e) => setNotesCommande(e.target.value)}
              style={tunnelStyles.textarea}
            />
          </section>
        </div>

        <aside
          className="panier-summary"
          style={{
            ...tunnelStyles.summary,
            ...(isMobileLayout
              ? {
                  position: "static",
                  top: "auto",
                  padding: 16,
                }
              : {}),
          }}
        >
          <h2 style={{ margin: "0 0 10px", fontSize: "1rem", lineHeight: 1.2 }}>
            {t.summary || "RÉSUMÉ"}
          </h2>

          <div className="summary-line">
            <span>
              {t.items || "Articles"} ({panier.length})
            </span>
            <span>{formatCHF(totalItemsCents)}</span>
          </div>

          {modeLivraison === "livraison" ? (
            <div className="summary-line">
              <span>Livraison</span>
              <span
                style={{
                  color: fraisPort === 0 ? "#27ae60" : "inherit",
                  fontWeight: "bold",
                }}
              >
                {fraisPort === 0 ? t.free || "GRATUIT" : formatCHF(fraisPort)}
              </span>
            </div>
          ) : null}

          {modeLivraison === "livraison" && jourLivraison ? (
            <div className="summary-line">
              <span>Jour de livraison</span>
              <span>{jourLivraison}</span>
            </div>
          ) : null}

          {modeLivraison === "pickpay" && horaireRecuperation ? (
            <div className="summary-line">
              <span>Créneau retrait</span>
              <span>{horaireRecuperation}</span>
            </div>
          ) : null}

          <hr style={{ margin: "10px 0" }} />

          <div className="summary-line total">
            <span>{t.totalToPay || "TOTAL FINAL"}</span>
            <span className="total-amount">{formatCHF(totalFinal)}</span>
          </div>

          <div style={tunnelStyles.paymentBox}>
            <div style={{ fontWeight: 800 }}>
              {t.securePaymentTitle || "Paiement sécurisé"}
            </div>

            <div style={tunnelStyles.paymentText}>
              Le bouton de paiement vous redirige vers une page PostFinance
              Checkout sécurisée pour finaliser votre commande.
            </div>
          </div>

          <button
            className="btn-checkout"
            disabled={isProcessing}
            onClick={executerPaiementEtValidation}
            style={tunnelStyles.checkoutButton}
            type="button"
          >
            {isProcessing
              ? t.redirectingPayment || "REDIRECTION VERS LE PAIEMENT..."
              : `${t.pay || "PAYER"} ${formatCHF(totalFinal)}`}
          </button>

          <button
            className="btn-back-link"
            onClick={() => setVue("panier")}
            style={tunnelStyles.backButton}
            type="button"
          >
            ← {t.backToCart || "Retour au panier"}
          </button>
        </aside>
      </div>
    </div>
  );
};

export default TunnelCommande;